import React, { useEffect } from 'react';
import { Link, Link as RouterLink } from 'react-router-dom';
import ArtworkList from '../components/artwork/ArtworkList';
import SEOHead from '../components/seo/SEOHead';

const HomePage: React.FC = () => {
    // ... same useEffect ...
    useEffect(() => {
        const handleHashScroll = () => {
            const hash = window.location.hash;
            if (hash === '#collectie') {
                const element = document.getElementById('collectie');
                if (element) {
                    setTimeout(() => {
                        element.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                }
            }
        };

        handleHashScroll();
        window.addEventListener('hashchange', handleHashScroll);
        return () => window.removeEventListener('hashchange', handleHashScroll);
    }, []);


    const homeSchema = [
        {
            "@context": "https://schema.org",
            "@type": "Person",
            "@id": "https://www.kunstlounge.nl/over-ons#clemens-briels",
            "name": "Clemens Briels",
            "birthDate": "1946",
            "nationality": "NL",
            "jobTitle": "Beeldend kunstenaar",
            "description": "Grondlegger van het Antipodisme. Officieel Olympisch Kunstenaar bij de Winterspelen van Salt Lake City 2002. Werken in collecties van KLM, Philips, ABN AMRO en Ford.",
            "url": "https://www.kunstlounge.nl/over-ons",
            "sameAs": ["https://kunstlounge.nl/over-ons"],
            "knowsAbout": ["Antipodisme", "Zeefdrukken", "Acrylic painting", "Beeldende kunst"],
            "award": "Officieel Olympisch Kunstenaar Winterspelen Salt Lake City 2002"
        },
        {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Kunstlounge",
            "alternateName": "Kunstlounge.nl",
            "url": "https://www.kunstlounge.nl",
            "description": "Exclusieve privécollectie van Clemens Briels. Originele schilderijen en gelimiteerde zeefdrukken van de grondlegger van het Antipodisme.",
            "inLanguage": "nl-NL",
            "publisher": {
                "@type": "Organization",
                "name": "Kunstlounge",
                "url": "https://www.kunstlounge.nl",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://www.kunstlounge.nl/images/logo.png"
                }
            },
            "potentialAction": {
                "@type": "SearchAction",
                "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": "https://www.kunstlounge.nl/artwork/{search_term_string}"
                },
                "query-input": "required name=search_term_string"
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "Wat is Kunstlounge.nl?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Kunstlounge.nl is de privécollectie van een particuliere liefhebber van het werk van Clemens Briels. Originele schilderijen, gelimiteerde zeefdrukken en 3D-wandobjecten worden rechtstreeks aangeboden aan nieuwe verzamelaars, buiten het galeriecircuit om. Elk werk komt met een door Clemens Briels persoonlijk gesigneerd certificaat van echtheid."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Waarom een privécollectie in plaats van een galerie?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Een galerie rekent doorgaans opslagkosten en commissie door in de prijs. Kunstlounge.nl biedt werken rechtstreeks aan vanuit een particuliere verzameling, zonder tussenkomst van galerie of veilinghuis. Dat houdt het proces persoonlijk en de prijs transparant."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Voor wie is Kunstlounge.nl bedoeld?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Kunstlounge.nl richt zich op verzamelaars en liefhebbers die op zoek zijn naar authentiek werk van Clemens Briels: een origineel schilderij, een gelimiteerde zeefdruk of een 3D-wandobject. De werken zijn geschikt voor particuliere collecties en interieurs waar kleur en het Antipodisme een plek verdienen."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Hoe verloopt het traject van aanvraag tot aankoop in hoofdlijnen?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Een aanvraag verloopt in vier stappen: eerst dient u een aanvraag in via het contactformulier, daarna volgt persoonlijk contact, vervolgens is bezichtiging op afspraak mogelijk of kunt u direct beslissen, en tot slot wordt het werk verzekerd en professioneel verpakt geleverd. Het volledige proces staat beschreven op de aanvraagpagina."
                    }
                }
            ]
        }
    ];

    return (
        <main id="main-content" className="pt-20">
            <SEOHead
                title="Clemens Briels schilderijen & zeefdrukken kopen | Kunstlounge"
                description="Exclusieve privécollectie originele schilderijen en zeefdrukken van Clemens Briels. Kleurrijk, optimistisch en direct uit de bron voor de serieuze verzamelaar."
                canonical="/"
                ogType="website"
                schemaData={homeSchema}
                hreflangEn="/en/"
                hreflangNl="/"
                lang="nl"
            />
            {/* Hero Section */}
            <section className="relative h-[90vh] min-h-[500px] flex items-center justify-center overflow-hidden bg-white">
                <div className="absolute inset-0 opacity-5 bg-cover bg-center grayscale" style={{ backgroundImage: "url('/images/collectie/antigua-a-bay-a-day/antigua-a-bay-a-day-1.webp')", contain: 'layout' }} aria-hidden="true"></div>
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-gray-50 to-transparent pointer-events-none" aria-hidden="true"></div>

                <div className="text-center px-6 relative z-10 max-w-5xl mx-auto">
                    <span className="block text-accent-light text-xs tracking-[0.4em] uppercase mb-8 animate-fade-in-up">
                        Exclusieve Privé Collectie
                    </span>
                    <h1 className="text-4xl sm:text-6xl md:text-8xl font-serif text-primary mb-10 leading-tight animate-fade-in-up delay-300">
                        Clemens Briels<br />
                        <span className="text-gray-300 font-light italic text-2xl sm:text-4xl md:text-6xl">Geselecteerde Werken, Beschikbaar voor Overname</span>
                    </h1>

                    <p className="text-xl text-gray-500 max-w-3xl mx-auto mb-14 leading-relaxed font-light animate-fade-in-up delay-500">
                        Ontdek een unieke selectie van originele schilderijen en zeefdrukken.<br />
                        Kleurrijk, optimistisch en direct uit de bron voor de serieuze verzamelaar.
                    </p>
                    <div className="animate-fade-in-up delay-700 flex flex-col items-center">
                        <a
                            href="#collectie"
                            onClick={(e) => {
                                e.preventDefault();
                                document.getElementById('collectie')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="group flex flex-col items-center text-[10px] uppercase tracking-[0.3em] text-primary hover:text-accent transition-all duration-500 cursor-pointer"
                        >
                            Bekijk de Collectie
                            <span className="mt-4 w-px h-16 bg-gray-200 group-hover:bg-accent transition-all duration-500" aria-hidden="true"></span>
                        </a>
                    </div>
                </div>
            </section>

            {/* Intro SEO Section */}
            <section className="py-12 bg-gray-50 border-b border-gray-100">
                <div className="container mx-auto px-6 max-w-4xl text-center">
                    <p className="text-gray-600 text-base leading-relaxed font-light">
                        Kunstlounge biedt een zorgvuldig samengestelde privécollectie van originele schilderijen en zeefdrukken van Clemens Briels, grondlegger van het Antipodisme en officieel Olympisch Kunstenaar (Salt Lake City 2002). Of u nu een kleurrijk schilderij wilt kopen, een originele zeefdruk zoekt of overweegt te investeren in moderne kunst: hier vindt u uitsluitend authentiek werk, direct uit de bron, met certificaat van echtheid.
                    </p>
                </div>
            </section>

            {/* USP Section */}
            <section className="py-24 bg-white border-y border-gray-100">
                <div className="container mx-auto px-6">
                    <h2 className="sr-only">Waarom Kunstlounge</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
                        <div className="text-center">
                            <div className="text-accent-light text-2xl mb-4">&#10004;</div>
                            <h3 className="font-serif text-primary mb-2">100% Origineel</h3>
                            <p className="text-gray-400 text-sm font-light">Direct uit privécollectie, gegarandeerde authenticiteit.</p>
                        </div>
                        <div className="text-center">
                            <div className="text-accent-light text-2xl mb-4">&#10004;</div>
                            <h3 className="font-serif text-primary mb-2">Met Certificaat</h3>
                            <p className="text-gray-400 text-sm font-light">Inclusief officieel certificaat van echtheid voor elk werk.</p>
                        </div>
                        <div className="text-center">
                            <div className="text-accent-light text-2xl mb-4">&#10004;</div>
                            <h3 className="font-serif text-primary mb-2">Persoonlijk Contact</h3>
                            <p className="text-gray-400 text-sm font-light">Geen galerie-opslag of onnodige commissies.</p>
                        </div>
                        <div className="text-center">
                            <div className="text-accent-light text-2xl mb-4">&#10004;</div>
                            <h3 className="font-serif text-primary mb-2">Bezichtiging Mogelijk</h3>
                            <p className="text-gray-400 text-sm font-light">Bekijk de werken op afspraak in onze besloten lounge.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Hoe werkt het */}
            <section className="py-24 bg-gray-50" aria-labelledby="process-heading">
                <div className="container mx-auto px-6 max-w-5xl">
                    <div className="flex flex-col items-center mb-16">
                        <span className="w-12 h-px bg-accent-light mb-8" aria-hidden="true"></span>
                        <h2 id="process-heading" className="text-3xl md:text-4xl font-serif text-primary text-center">Hoe Werkt het?</h2>
                        <p className="mt-4 text-gray-400 font-light text-sm tracking-wide text-center max-w-xl">
                            Van eerste interesse tot kunstwerk aan uw muur, persoonlijk en transparant.
                        </p>
                    </div>
                    <ol className="grid grid-cols-1 md:grid-cols-4 gap-10" role="list">
                        {[
                            { step: '01', title: 'Aanvraag', body: 'U vraagt informatie aan via het formulier of voegt een werk toe aan uw aanvraaglijst.' },
                            { step: '02', title: 'Persoonlijk Contact', body: 'Wij nemen binnen 24 uur contact op. Extra foto\'s, technische details of vragen, alles op maat.' },
                            { step: '03', title: 'Bezichtiging of Akkoord', body: 'Bezichtiging op afspraak is mogelijk. Liever direct beslissen? Dat kan ook.' },
                            { step: '04', title: 'Verzekerde Levering', body: 'Het werk wordt professioneel verpakt en verzekerd geleverd. Geen gedoe, geen verborgen kosten.' },
                        ].map(({ step, title, body }) => (
                            <li key={step} className="flex flex-col items-center text-center">
                                <span className="text-accent-light font-serif text-4xl mb-4 leading-none">{step}</span>
                                <h3 className="font-serif text-primary text-lg mb-3">{title}</h3>
                                <p className="text-gray-400 text-sm font-light leading-relaxed">{body}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            <section id="collectie" className="py-32 bg-white" aria-labelledby="collection-heading">
                <div className="container mx-auto px-6">
                    <div className="flex flex-col items-center mb-24">
                        <span className="w-12 h-px bg-accent-light mb-8" aria-hidden="true"></span>
                        <h2 id="collection-heading" className="text-4xl md:text-5xl font-serif text-primary text-center">De Meesterwerken</h2>
                        <p className="mt-6 text-gray-500 font-light italic text-base max-w-2xl text-center">
                            Verken onze zorgvuldig opgebouwde verzameling van schilderijen, zeefdrukken en wandobjecten van de internationaal gelauwerde kunstenaar Clemens Briels.
                        </p>
                    </div>

                    <ArtworkList />
                </div>
            </section>

            {/* Antipodisme Deep Dive */}
            <section className="py-32 bg-gray-50" aria-labelledby="artist-concept-heading">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
                        <div className="order-2 md:order-1">
                            <h2 id="artist-concept-heading" className="text-4xl font-serif text-primary mb-10 leading-snug">
                                Ontdek het <span className="text-accent-light italic">Antipodisme</span>
                            </h2>
                            <p className="text-gray-600 leading-relaxed mb-6 font-light text-lg">
                                Clemens Briels, de maestro achter het Antipodisme, nodigt u uit in een wereld waar alle regels aan de laars worden gelapt. Zijn werk is een frontale botsing van contrasten die u met een glimlach achterlaat.
                            </p>
                            <p className="text-gray-600 leading-relaxed mb-8 font-light">
                                Geboren in 1946 maar met een ontembare energie, creëert Briels kunst die een shot optimisme rechtstreeks in uw aderen is. Van zinderende kleuren tot speelse realiteitsvervormingen; elk werk viert de onversneden levensvreugde.
                            </p>
                            <div className="flex gap-6">
                                <RouterLink to="/antipodisme" className="text-[10px] uppercase tracking-widest text-primary border-b border-primary pb-2 hover:text-accent hover:border-accent transition-colors">Lees meer over de stijl</RouterLink>
                                <RouterLink to="/over-ons" className="text-[10px] uppercase tracking-widest text-primary border-b border-primary pb-2 hover:text-accent hover:border-accent transition-colors">Over de kunstenaar</RouterLink>
                            </div>
                        </div>
                        <div className="order-1 md:order-2 relative aspect-square bg-white p-4 shadow-2xl rotate-1 group hover:rotate-0 transition-transform duration-1000">
                            {/* ... image ... */}
                            <img
                                src="/images/collectie/you-carved-out-your-name/you-carved-out-your-name.jpg"
                                alt="Clemens Briels Antipodisme Kunstwerk"
                                className="w-full h-full object-cover grayscale brightness-110 contrast-125"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-32 bg-white" aria-labelledby="faq-heading">
                <div className="container mx-auto px-6 max-w-4xl">
                    <h2 id="faq-heading" className="text-4xl font-serif text-primary mb-16 text-center">Veelgestelde Vragen</h2>
                    <div className="space-y-12">
                        <div>
                            <h3 className="text-xl font-serif text-primary mb-4">Wat is Kunstlounge.nl?</h3>
                            <p className="text-gray-500 font-light leading-relaxed">
                                Kunstlounge.nl is de privécollectie van een particuliere liefhebber van het werk van Clemens Briels. Originele schilderijen, gelimiteerde zeefdrukken en 3D-wandobjecten worden rechtstreeks aangeboden aan nieuwe verzamelaars, buiten het galeriecircuit om. Elk werk komt met een door Clemens Briels persoonlijk gesigneerd certificaat van echtheid.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-xl font-serif text-primary mb-4">Waarom een privécollectie in plaats van een galerie?</h3>
                            <p className="text-gray-500 font-light leading-relaxed">
                                Een galerie rekent doorgaans opslagkosten en commissie door in de prijs. Kunstlounge.nl biedt werken rechtstreeks aan vanuit een particuliere verzameling, zonder tussenkomst van galerie of veilinghuis. Dat houdt het proces persoonlijk en de prijs transparant.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-xl font-serif text-primary mb-4">Voor wie is Kunstlounge.nl bedoeld?</h3>
                            <p className="text-gray-500 font-light leading-relaxed">
                                Kunstlounge.nl richt zich op verzamelaars en liefhebbers die op zoek zijn naar authentiek werk van Clemens Briels: een origineel schilderij, een gelimiteerde zeefdruk of een 3D-wandobject. De werken zijn geschikt voor particuliere collecties en interieurs waar kleur en het Antipodisme een plek verdienen.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-xl font-serif text-primary mb-4">Hoe verloopt het traject van aanvraag tot aankoop in hoofdlijnen?</h3>
                            <p className="text-gray-500 font-light leading-relaxed">
                                Een aanvraag verloopt in vier stappen: eerst dient u een aanvraag in via het contactformulier, daarna volgt persoonlijk contact, vervolgens is bezichtiging op afspraak mogelijk of kunt u direct beslissen, en tot slot wordt het werk verzekerd en professioneel verpakt geleverd. Het volledige proces staat beschreven op de aanvraagpagina.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-40 bg-gray-50 text-center" aria-labelledby="cta-heading">
                <div className="container mx-auto px-6">
                    <h2 id="cta-heading" className="text-4xl md:text-5xl font-serif mb-10 text-primary">Interesse in een Privé Bezichtiging?</h2>
                    <p className="text-gray-500 max-w-2xl mx-auto mb-16 text-lg font-light leading-relaxed">
                        Ervaar de textuur, de diepte en de ware kleuren van de meesterwerken in onze besloten lounge. <br />
                        Geen galeriedruk, maar persoonlijk contact.
                    </p>
                    <RouterLink
                        to="/aanvraag"
                        className="inline-block px-12 py-5 bg-primary text-white hover:bg-accent transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold shadow-xl hover:shadow-accent/20"
                        aria-label="Maak een afspraak voor een privé bezichtiging"
                    >
                        Neem Contact Op
                    </RouterLink>
                </div>
            </section>
        </main>
    );
};

export default HomePage;